<template>
  <app-collapse class="mt-1">
    <app-collapse-item
      :is-visible="false"
      class="collapsible-section-bg"
      title="Supplementary units"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasSupplementaryUnitsData"
            :has-errors="item.issues && item.issues.length > 0"
          />
          Supplementary units
        </span>
      </template>
      <div
        v-for="(unit, unitIndex) in item.supplementary_units"
        :key="unitIndex"
        class="border rounded pl-3 pr-3 pt-1 mb-2"
      >
        <b-row>
          <b-col
            cols="12"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Supplementary unit"
              rules=""
            >
              <b-form-group>
                <label>
                  Supplementary unit <small class="text-muted">180200000A</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Type of supplementary measurement unit'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <classifier-select
                  v-model="unit.supplementary_unit"
                  :classifier-key="classifierKeys.measurementUnits"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Quantity"
            >
              <b-form-group>
                <label>
                  Quantity
                  <small class="text-muted">180200000C</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Quantity in supplementary units'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="unit.quantity"
                  type="number"
                  step="0.001"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Measurement qualifier"
            >
              <b-form-group>
                <label>
                  Measurement qualifier
                  <small class="text-muted">180200000B</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Qualifier for the measurement'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <classifier-select
                  v-model="unit.measurement_qualifier"
                  :classifier-key="classifierKeys.measurementQualifiers"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="1"
            class="text-right"
          >
            <b-button
              v-if="!disabled"
              variant="link"
              size="sm"
              class="text-danger mt-2"
              @click="removeUnit(unitIndex)"
            >
              <feather-icon icon="TrashIcon" />
              Delete
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-row>
        <b-col class="text-right">
          <b-button
            v-if="!disabled"
            variant="info"
            size="sm"
            @click="addUnit"
          >
            <feather-icon icon="PlusIcon" />
            New unit
          </b-button>
        </b-col>
      </b-row>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'
import ClassifierSelect from '@/views/components/ClassifierSelect.vue'
import { CUSTOMS_CLASSIFIER_KEYS } from '@/libs/customsClassifiers'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    ValidationProvider,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
    ClassifierSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { classifierKeys: CUSTOMS_CLASSIFIER_KEYS }
  },
  computed: {
    hasSupplementaryUnitsData() {
      if (!this.item || !this.item.supplementary_units) {
        return false
      }
      return this.item.supplementary_units.some(unit => unit.supplementary_unit || unit.measurement_qualifier || unit.quantity)
    },
  },
  methods: {
    addUnit() {
      if (!this.item.supplementary_units) {
        this.$set(this.item, 'supplementary_units', [])
      }
      this.item.supplementary_units.push({
        supplementary_unit: '',
        measurement_qualifier: '',
        quantity: '',
      })
    },
    removeUnit(unitIndex) {
      this.$bvModal.msgBoxConfirm('Are you sure you want to delete this supplementary unit?', {
        title: 'Please Confirm',
        size: 'sm',
        okVariant: 'danger',
        okTitle: 'Yes',
        cancelTitle: 'No',
        centered: true,
      }).then(value => {
        if (value) {
          this.item.supplementary_units.splice(unitIndex, 1)
        }
      })
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}
</style>
